import type { JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';

import { findParentSection, findSectionBySlug } from '../navigation';

/**
 * DocBreadcrumb - Shows current location in documentation hierarchy.
 * Format: "Docs > Section > Subsection"
 */
export function DocBreadcrumb(): JSX.Element {
  const { sectionSlug, subSlug } = useParams<{
    sectionSlug?: string;
    subSlug?: string;
  }>();

  // Build breadcrumb items
  const items: Array<{ href?: string; label: string }> = [{ label: 'Docs', href: '/docs' }];

  if (sectionSlug) {
    // Find the section
    const section = findSectionBySlug(sectionSlug);

    // Check if this is a child section - find its parent
    const parentSection = findParentSection(sectionSlug);

    if (parentSection) {
      // Add parent section
      items.push({
        label: parentSection.title,
        href: `/docs/${parentSection.slug}`,
      });
    }

    if (section) {
      items.push({
        label: section.title,
        href: subSlug ? `/docs/${sectionSlug}` : undefined, // Link if we have a subsection
      });
    }
  }

  if (subSlug) {
    const subSection = findSectionBySlug(subSlug);
    if (subSection) {
      items.push({ label: subSection.title });
    }
  }

  return (
    <nav aria-label="Breadcrumb" className="mb-6">
      <ol className="flex items-center gap-1 text-sm">
        {items.map((item, index) => (
          <li key={index} className="flex items-center gap-1">
            {index > 0 && <ChevronRightIcon className="h-4 w-4 text-muted" aria-hidden="true" />}

            {item.href ? (
              <Link
                to={item.href}
                className="text-muted hover:text-foreground transition-colors flex items-center gap-1"
              >
                {index === 0 && <HomeIcon className="h-4 w-4" />}
                {item.label}
              </Link>
            ) : (
              <span className="text-foreground font-medium flex items-center gap-1">
                {index === 0 && <HomeIcon className="h-4 w-4" />}
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
